<template>
  <div class="w-full" ref="container">
    <VDropdown
      :distance="6"
      theme="dropdown"
      placement="bottom"
      no-auto-focus
      ref="tooltip"
      :autoHide="hasDefaultSlot"
      :delay="{ show: 0, hide: 250 }"
    >
      <template #default="{ shown }">
        <div class="w-full">
          <div
            class="w-full"
            @click="
              e => {
                if (!hasDefaultSlot) e.stopPropagation()
              }
            "
          >
            <TextInput
              v-show="!hasDefaultSlot"
              :value="inputText"
              @input="onInputTextChanged"
              ref="textInput"
              :preventKeyDown="preventKeyDown"
              :label="label"
              :detailText="detailText"
              :required="required"
              :disabled="disabled"
              :placeHolder="placeHolder"
              :prefix="prefix"
              :suffix="suffix"
              :alwaysShowPrefix="alwaysShowPrefix"
              :alwaysShowSuffix="alwaysShowSuffix"
              :validate="internalValidate"
              @focus="$refs.tooltip.show()"
              @blur="$refs.tooltip.hide()"
            />
            <slot
              :dropdownOpened="shown"
              :show="
                () => {
                  $refs.tooltip?.show()
                }
              "
              :hide="
                () => {
                  $refs.tooltip?.hide()
                }
              "
            />
          </div>
        </div>
      </template>
      <template #popper="{ hide }">
        <div
          class="overflow-auto max-h-[40vh] flex flex-col"
          v-if="$refs.container"
          :style="{
            width: hasDefaultSlot
              ? 'max-content'
              : `${$refs.container.offsetWidth}px`,
          }"
        >
          <div
            class="relative text-start px-8 py-3 text-sm cursor-pointer selectItem w-full hover:bg-slate-100"
            v-for="(option, i) in options"
            :key="i"
            @click="
              () => {
                if (preventOptionSelect) return
                selectOption(option)
                hide()
              }
            "
          >
            <div class="w-full truncate">
              <span v-html="formatOptionForDropdownDisplay(option)"> </span>
            </div>
          </div>
        </div>
      </template>
    </VDropdown>
  </div>
</template>

<script>
import { defineComponent } from 'vue'
import TextInput from './TextInput.vue'
import Commons from '../../../shared/utils/commons'

export default defineComponent({
  name: 'SelectInput',
  components: {
    TextInput,
  },
  props: {
    value: {
      type: [String, Number, Boolean, Object],
      default: '',
    },
    modelValue: {
      type: [String, Number, Boolean, Object],
      default: '',
    },
    preventKeyDown: {
      type: Boolean,
      default: true,
    },
    label: {
      type: String,
      default: '',
    },
    detailText: {
      type: String,
      default: '',
    },
    required: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    placeHolder: {
      type: String,
      default: '',
    },
    prefix: {
      type: String,
      default: '',
    },
    suffix: {
      type: String,
      default: '',
    },
    alwaysShowPrefix: {
      type: Boolean,
      default: false,
    },
    alwaysShowSuffix: {
      type: Boolean,
      default: false,
    },
    validate: {
      type: Function,
      default: () => {
        return ''
      },
    },
    options: {
      type: Array,
      default: () => {
        return []
      },
    },
    optionsAreSame: {
      type: Function,
      default: null,
    },
    formatOptionsDisplay: {
      type: Function,
      default: null,
    },
    formatInputToValue: {
      type: Function,
      default: null,
    },
    formatOptionInputDisplay: {
      type: Function,
      default: null,
    },
    valueMustBeInOptions: {
      type: Boolean,
      default: true,
    },
    preventOptionSelect: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      // inputText: "",
      selectedOption: null,
      isShown: false,
    }
  },
  mounted() {
    this.reloadInputContent()
  },
  methods: {
    checkIsSameOptions(opt1, opt2) {
      if (this.optionsAreSame) return this.optionsAreSame(opt1, opt2)
      const str1 = JSON.stringify(opt1)
      const str2 = JSON.stringify(opt2)
      return str1 == str2
    },
    reloadInputContent() {
      this.selectedOption = JSON.parse(
        JSON.stringify(this.modelValue || this.value),
      )
    },
    externalSetValue(newValue) {
      if (this.checkIsSameOptions(newValue, this.selectedOption)) return
      this.selectedOption = JSON.parse(JSON.stringify(newValue))
    },
    formatOptionForDropdownDisplay(option) {
      let res = option
      if (this.formatOptionsDisplay) res = this.formatOptionsDisplay(option)
      return `${res}`
    },
    formatOptionForInputDisplay(option) {
      let res = option
      if (this.formatOptionsDisplay) res = this.formatOptionsDisplay(option)
      if (this.formatOptionInputDisplay)
        res = this.formatOptionInputDisplay(option)
      return `${res}`
    },
    selectOption(option) {
      this.selectedOption = JSON.parse(JSON.stringify(option))
    },
    onInputTextChanged(value) {
      this.$emit('inputTextChanged', value)
      const matchingOption = this.findMatchingOption(value)
      if (matchingOption.index == -1) {
        if (!this.formatInputToValue) this.selectedOption = value
        else this.selectedOption = this.formatInputToValue(value)
      } else {
        this.selectedOption = matchingOption.value
      }
    },
    internalValidate(value) {
      const matchingOption = this.findMatchingOption(value)
      let error = this.validate(matchingOption.value, matchingOption.index)
      error ||= this.validateValueIncludedInOptions(matchingOption)
      return error
    },
    validateValueIncludedInOptions(matchingOption) {
      const { index } = matchingOption
      if (!this.valueMustBeInOptions) return ''
      if (index == -1) return 'Veuillez sélectionner une valeur proposée'
      return ''
    },
    findMatchingOption(value) {
      const matchingIndex = this.inputValues.indexOf(value)
      return { index: matchingIndex, value: this.options[matchingIndex] }
    },
  },
  watch: {
    value(newValue) {
      if (this.checkIsSameOptions(newValue, this.selectedOption)) return
      this.selectedOption = JSON.parse(JSON.stringify(newValue))
    },
    modelValue(newValue) {
      if (this.checkIsSameOptions(newValue, this.selectedOption)) return
      this.selectedOption = JSON.parse(JSON.stringify(newValue))
    },
    selectedOption: {
      deep: true,
      handler(newValue, old) {
        this.$emit('update:modelValue', newValue)
        this.$emit('input:modelValue', newValue)
        this.$emit('input', newValue)
      },
    },
    options: {
      deep: true,
      handler() {
        this.$refs.textInput?.makeInputValidation()
      },
    },
  },
  computed: {
    inputText() {
      if (!this.selectedOption) return ''
      return this.formatOptionForInputDisplay(this.selectedOption)
    },
    inputValues() {
      if (!Array.isArray(this.options)) return []
      return this.options.map(this.formatOptionForInputDisplay)
    },
    hasDefaultSlot() {
      return Commons.slotHasContent(this.$slots.default)
    },
  },
})
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
@import url(./assets/css/input.css);
</style>
